import { defineStore, acceptHMRUpdate } from 'pinia';
import type { Parameters, UtmParams } from '@/types/parameters';
import { storeConfig } from '@/shared/storeConfig';

const A_MONTH_IN_MS = 60 * 60 * 24 * 30;

const parametersStore = () => {
  const isLoading = ref<boolean>(false);
  const parameters = ref<Parameters[]>([]);
  const utm = ref<UtmParams>({});
  const referenceCodeFromStore = ref<string>('');

  const referenceCodeFromCookie = useCookie('ref_code', {
    default: () => '',
    path: '/',
    maxAge: A_MONTH_IN_MS,
  });

  const referenceCode = computed<string>({
    get() {
      return referenceCodeFromCookie.value || referenceCodeFromStore.value;
    },

    set(code: string) {
      referenceCodeFromCookie.value = code;
      referenceCodeFromStore.value = code;
    },
  });

  function setReferenceCode(code?: string) {
    if (!code) {
      return;
    }

    referenceCode.value = code;
  }

  function setCookie(name: string, value: string = '', days: number = 30) {
    const date = new Date();

    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    const expires = `expires=${date.toUTCString()}`;
    const config = `path=/;Secure;SameSite=none;${expires}`;

    document.cookie = `${name}=${value};${config}`;
  }

  function setUTMParams(params: UtmParams): void {
    const entries = objectEntries<UtmParams>(params);

    if (!entries.length) {
      return;
    }

    entries.forEach(([key, value]: [any, any]) => {
      localStorage.setItem(key, JSON.stringify(value));
      (utm.value as any)[key] = value;
      setCookie(key, String(value), 30);
    });
  }

  function $reset() {
    isLoading.value = false;
    parameters.value = [];
    utm.value = {};
    referenceCode.value = '';
  }

  return {
    $reset,
    isLoading,
    parameters,
    referenceCode,
    referenceCodeFromStore,
    setReferenceCode,
    utm,
    setUTMParams,
  };
};

export const useParametersStore = defineStore(
  'parameters',
  parametersStore,
  storeConfig,
);

// @ts-ignore
if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useParametersStore, import.meta.hot));
}
